// Toast deduplication utility to prevent spam
export class ToastDeduplicator {
  private static instance: ToastDeduplicator;
  private activeToasts = new Map<string, { timestamp: number; toastId: string }>();
  private cooldowns = new Map<string, number>();
  
  // Minimum time between showing the same type of toast (in milliseconds)
  private readonly COOLDOWN_DURATION = 30000; // 30 seconds
  private readonly MAX_TOAST_AGE = 10000; // 10 seconds to consider a toast "active"

  static getInstance(): ToastDeduplicator {
    if (!ToastDeduplicator.instance) {
      ToastDeduplicator.instance = new ToastDeduplicator();
    }
    return ToastDeduplicator.instance;
  }

  private constructor() {
    // Clean up old entries periodically
    setInterval(() => {
      this.cleanup();
    }, 60000); // Clean up every minute
  }

  private cleanup() {
    const now = Date.now();
    
    // Remove old active toasts
    for (const [key, value] of this.activeToasts.entries()) {
      if (now - value.timestamp > this.MAX_TOAST_AGE) {
        this.activeToasts.delete(key);
      }
    }
    
    // Remove old cooldowns
    for (const [key, timestamp] of this.cooldowns.entries()) {
      if (now - timestamp > this.COOLDOWN_DURATION) {
        this.cooldowns.delete(key);
      }
    }
  }

  canShowToast(toastType: string, title: string): boolean {
    const key = `${toastType}:${title}`;
    const now = Date.now();
    
    // Check if we have an active toast of this type
    if (this.activeToasts.has(key)) {
      console.log(`🚫 Toast blocked (already active): ${key}`);
      return false;
    }
    
    // Check cooldown
    const lastShown = this.cooldowns.get(key);
    if (lastShown && (now - lastShown) < this.COOLDOWN_DURATION) {
      console.log(`🚫 Toast blocked (cooldown): ${key}, remaining: ${Math.round((this.COOLDOWN_DURATION - (now - lastShown)) / 1000)}s`);
      return false;
    }
    
    return true;
  }

  registerToast(toastType: string, title: string, toastId: string) {
    const key = `${toastType}:${title}`;
    const now = Date.now();
    
    this.activeToasts.set(key, { timestamp: now, toastId });
    this.cooldowns.set(key, now);
    
    console.log(`✅ Toast registered: ${key} (id: ${toastId})`);
  }

  dismissToast(toastType: string, title: string) {
    const key = `${toastType}:${title}`;
    this.activeToasts.delete(key);
    console.log(`🗑️ Toast dismissed: ${key}`);
  }

  // Specialized method for connection errors
  canShowConnectionError(): boolean {
    return this.canShowToast('connection-error', 'Connection issue');
  }

  registerConnectionError(toastId: string) {
    this.registerToast('connection-error', 'Connection issue', toastId);
  }

  dismissConnectionError() {
    this.dismissToast('connection-error', 'Connection issue');
  }
}

// Enhanced toast function with deduplication
export function createDedupedToast() {
  const deduplicator = ToastDeduplicator.getInstance();
  
  return function dedupedToast(
    toastFunction: (...args: unknown[]) => unknown,
    toastType: string,
    options: unknown
  ) {
    const title = options.title || 'Notification';
    
    if (!deduplicator.canShowToast(toastType, title)) {
      return null;
    }
    
    const result = toastFunction(options);
    
    if (result && result.id) {
      deduplicator.registerToast(toastType, title, result.id);
      
      // Auto-dismiss tracking when toast is manually dismissed
      const originalDismiss = result.dismiss;
      result.dismiss = () => {
        deduplicator.dismissToast(toastType, title);
        return originalDismiss();
      };
    }
    
    return result;
  };
}